import { useCallback, useEffect, useState } from 'react';

import { AgGridReact, AgGridColumn } from 'ag-grid-react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import s from './AgGrid.module.scss';

const AgGrid = ({
  rowData,
  columnDefs,
  defaultSort,
  onGridReady,
  onCellClicked,
  onSortChanged,
  getRowNodeId,
  defaultColDef = {
    autoHeight: true,
    resizable: true,
  },
  rowSelection,
  immutableData,
  suppressHorizontalScroll,
  rowStyle,
  className,
  frameworkComponents,
  enableCellTextSelection,
  onRowSelected,
}) => {
  const [gridApi, setGridApi] = useState<any>(null);
  const [gridColumnApi, setGridColumnApi] = useState<any>(null);

  const autoSizeColumns = columnApi => {
    const skipHeader = false;
    const allColumnIds: Array<any> = [];
    columnApi.getAllColumns().forEach(column => {
      allColumnIds.push(column.colId);
    });
    columnApi.autoSizeColumns(allColumnIds, skipHeader);
  };

  const applySortDirection = columnApi => {
    if (defaultSort.length > 1 && defaultSort[0]) {
      columnApi
        .getColumn(defaultSort[0].replace(':', '.'))
        .setSort(defaultSort[1]);
    }
  };

  const gridReady = params => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);

    applySortDirection(params.columnApi);

    if (window.innerWidth > 768) {
      params.api.sizeColumnsToFit();
      return onGridReady(params.api);
    }

    autoSizeColumns(params.columnApi);

    return onGridReady(params.api);
  };

  const gridResize = useCallback(() => {
    if (!gridApi) return;

    if (window.innerWidth > 768) {
      // obs: sizeColumnsToFit() warning message still there, it is an issue of aggrid
      gridApi.sizeColumnsToFit();
    }
  }, [gridApi]);

  useEffect(() => {
    window.addEventListener('resize', gridResize);

    return () => window.removeEventListener('resize', gridResize);
  }, [gridResize]);

  useEffect(() => {
    if (!gridColumnApi) return;

    gridColumnApi.resetColumnState();

    applySortDirection(gridColumnApi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSort]);

  return (
    <div className={cx('ag-theme-material EntityTable', className, s.grid)}>
      <AgGridReact
        rowClass={cx({ 'row-pointer': onCellClicked })}
        rowData={rowData}
        onGridReady={gridReady}
        enableCellTextSelection={enableCellTextSelection}
        onCellClicked={onCellClicked}
        onSortChanged={onSortChanged}
        getRowNodeId={getRowNodeId}
        defaultColDef={defaultColDef}
        rowSelection={rowSelection}
        immutableData={immutableData}
        suppressHorizontalScroll={suppressHorizontalScroll}
        rowStyle={rowStyle}
        frameworkComponents={frameworkComponents}
        onRowSelected={onRowSelected}
      >
        {columnDefs.map(({ ...column }) => (
          <AgGridColumn {...column} key={column.field} />
        ))}
      </AgGridReact>
    </div>
  );
};

AgGrid.propTypes = {
  frameworkComponents: PropTypes.any,
  rowData: PropTypes.array,
  columnDefs: PropTypes.array,
  defaultSort: PropTypes.array,
  onGridReady: PropTypes.func,
  onCellClicked: PropTypes.func,
  onSortChanged: PropTypes.func,
  getRowNodeId: PropTypes.func,
  defaultColDef: PropTypes.shape({
    autoHeight: PropTypes.bool,
    resizable: PropTypes.bool,
  }),
  rowSelection: PropTypes.string,
  immutableData: PropTypes.bool,
  suppressHorizontalScroll: PropTypes.bool,
  rowStyle: PropTypes.object,
  className: PropTypes.string,
  onRowSelected: PropTypes.func,
  enableCellTextSelection: PropTypes.bool,
};

AgGrid.defaultProps = {
  onRowSelected: undefined,
  frameworkComponents: undefined,
  rowData: [],
  columnDefs: [],
  defaultSort: [],
  onGridReady: () => {},
  onCellClicked: undefined,
  onSortChanged: undefined,
  getRowNodeId: undefined,
  defaultColDef: {
    autoHeight: true,
    resizable: true,
  },
  rowSelection: 'single',
  immutableData: false,
  suppressHorizontalScroll: false,
  rowStyle: {},
  className: undefined,
  enableCellTextSelection: false,
};

export default AgGrid;
